// src/pages/EditarSolicitud.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../components/Toast/useToast.jsx";
import { useAuth } from "../contexts/AuthContext";
import { db, serverTimestamp } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { isValidPhone } from "../utils";
import DatePicker from "../components/DatePicker";

export default function EditarSolicitud() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user, companyId, isCompanyAdmin, isSuperAdmin } = useAuth();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [solicitud, setSolicitud] = useState(null);
  const [operarios, setOperarios] = useState([]);
  const [selectedOperarios, setSelectedOperarios] = useState([]);
  const [filtroOperarios, setFiltroOperarios] = useState("");

  const [formData, setFormData] = useState({
    fechaInicio: "",
    fechaFin: "",
    numeroOferta: "",
    numeroPresupuesto: "",
    codigoObra: "",
    partidaTrabajo: "",
    trabajosRealizar: "",
    operariosAsignados: "",
    direccionObra: "",
    personaContacto: "",
    telefonoContacto: "",
  });

  const isAdmin = isCompanyAdmin() || isSuperAdmin();

  // Cargar solicitud
  useEffect(() => {
    loadSolicitud();
  }, [id]);

  // Cargar operarios
  useEffect(() => {
    fetch('/personal.json')
      .then(r => r.json())
      .then(data => setOperarios(data))
      .catch(() => setOperarios([]));
  }, []);

  const loadSolicitud = async () => {
    try {
      setLoadingData(true);
      const docRef = doc(db, "work_requests", id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        showToast({ type: "error", message: "Solicitud no encontrada" });
        navigate('/historial-solicitudes');
        return;
      }

      const data = { id: docSnap.id, ...docSnap.data() };

      // Verificar permisos: admin puede editar siempre, usuario solo si es suya y está en "enviada"
      const esAdmin = isCompanyAdmin() || isSuperAdmin();
      if (!esAdmin) {
        if (data.requesterId !== user?.uid) {
          showToast({ type: "error", message: "No tienes permisos para editar esta solicitud" });
          navigate('/historial-solicitudes');
          return;
        }
        if (data.status !== 'enviada') {
          showToast({ type: "error", message: "Esta solicitud ya no se puede editar porque está en proceso" });
          navigate(`/solicitud/${id}`);
          return;
        }
      }

      setSolicitud(data);
      setFormData({
        fechaInicio: data.fechaInicio || "",
        fechaFin: data.fechaFin || "",
        numeroOferta: data.numeroOferta || "",
        numeroPresupuesto: data.numeroPresupuesto || "",
        codigoObra: data.codigoObra || "",
        partidaTrabajo: data.partidaTrabajo || "",
        trabajosRealizar: data.trabajosRealizar || "",
        operariosAsignados: data.operariosAsignados || "",
        direccionObra: data.direccionObra || "",
        personaContacto: data.personaContacto || "",
        telefonoContacto: data.telefonoContacto || "",
      });
      setSelectedOperarios(data.selectedOperarios || []);
    } catch (error) {
      console.error("Error cargando solicitud:", error);
      showToast({ type: "error", message: "Error cargando la solicitud" });
      navigate('/historial-solicitudes');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const setRangoHoy = () => {
    const hoy = new Date().toISOString().split('T')[0];
    handleChange('fechaInicio', hoy);
    handleChange('fechaFin', hoy);
  };

  const setRangoEstaSemana = () => {
    const hoy = new Date();
    const inicio = new Date(hoy);
    const fin = new Date(hoy);
    const diaActual = inicio.getDay();
    const diasDesdeInicio = diaActual === 0 ? 6 : diaActual - 1;
    inicio.setDate(inicio.getDate() - diasDesdeInicio);
    const diasHastaFin = diaActual === 0 ? -2 : 5 - diaActual;
    fin.setDate(fin.getDate() + diasHastaFin);
    handleChange('fechaInicio', inicio.toISOString().split('T')[0]);
    handleChange('fechaFin', fin.toISOString().split('T')[0]);
  };

  const setRangoProximaSemana = () => {
    const hoy = new Date();
    const proximoLunes = new Date(hoy);
    const proximoViernes = new Date(hoy);
    const diasHastaLunes = (8 - hoy.getDay()) % 7 || 7;
    proximoLunes.setDate(hoy.getDate() + diasHastaLunes);
    proximoViernes.setDate(proximoLunes.getDate() + 4);
    handleChange('fechaInicio', proximoLunes.toISOString().split('T')[0]);
    handleChange('fechaFin', proximoViernes.toISOString().split('T')[0]);
  };

  const formatRangoFechas = () => {
    if (!formData.fechaInicio || !formData.fechaFin) return "";
    const inicio = new Date(formData.fechaInicio).toLocaleDateString('es-ES');
    const fin = new Date(formData.fechaFin).toLocaleDateString('es-ES');
    return formData.fechaInicio === formData.fechaFin ? inicio : `${inicio} - ${fin}`;
  };

  const addOperario = (operario) => {
    if (!operario || selectedOperarios.some(op => op.id === operario.id)) return;
    const newSelected = [...selectedOperarios, operario];
    setSelectedOperarios(newSelected);
    handleChange('operariosAsignados', newSelected.map(op => `${op.nombre} (${op.categoria} - ${op.especialidad})`).join(', '));
  };

  const removeOperario = (operarioId) => {
    const newSelected = selectedOperarios.filter(op => op.id !== operarioId);
    setSelectedOperarios(newSelected);
    handleChange('operariosAsignados', newSelected.map(op => `${op.nombre} (${op.categoria} - ${op.especialidad})`).join(', '));
  };

  const norm = (str) => str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const operariosFiltrados = operarios.filter(op =>
    norm(op.nombre).includes(norm(filtroOperarios))
  );

  const validateForm = () => {
    const required = ['fechaInicio', 'fechaFin', 'codigoObra', 'partidaTrabajo', 'trabajosRealizar', 'operariosAsignados', 'direccionObra', 'personaContacto'];
    const fieldNames = {
      fechaInicio: 'Fecha de inicio', fechaFin: 'Fecha de fin',
      codigoObra: 'Código de obra', partidaTrabajo: 'Partida de trabajo',
      trabajosRealizar: 'Trabajos a realizar', operariosAsignados: 'Operarios asignados',
      direccionObra: 'Dirección de obra', personaContacto: 'Persona de contacto'
    };
    for (const field of required) {
      if (!formData[field]?.trim()) {
        showToast({ type: "error", message: `El campo "${fieldNames[field]}" es obligatorio` });
        return false;
      }
    }
    if (formData.fechaInicio && formData.fechaFin && new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
      showToast({ type: "error", message: "La fecha de fin no puede ser anterior a la fecha de inicio" });
      return false;
    }
    if (formData.telefonoContacto && !isValidPhone(formData.telefonoContacto)) {
      showToast({ type: "error", message: "El formato del teléfono no es válido" });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await setDoc(doc(db, "work_requests", id), {
        ...formData,
        rangoFechas: formatRangoFechas(),
        selectedOperarios,
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      }, { merge: true });

      showToast({ type: "success", message: "Solicitud actualizada correctamente" });
      navigate(`/solicitud/${id}`);
    } catch (error) {
      console.error("Error actualizando solicitud:", error);
      showToast({ type: "error", message: `Error al guardar: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return <div className="container"><p style={{ padding: 24, textAlign: 'center' }}>Cargando...</p></div>;
  }

  if (!solicitud) return null;

  const inputStyle = { background: '#0d1330', border: '1px solid #273067', borderRadius: 12, color: '#e9eefc', padding: '10px 12px', width: '100%' };

  return (
    <div className="container">
      <div className="header">
        <span className="badge">FIRMAS CEJM</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button className="copy" onClick={() => navigate(`/solicitud/${id}`)}>← Volver</button>
        <h1 className="title" style={{ margin: 0, flex: 1 }}>✏️ Editar Solicitud</h1>
        {isAdmin && solicitud.status !== 'enviada' && (
          <span className="badge" style={{ background: '#f59e0b', color: 'white' }}>
            ⚠️ En proceso — solo admins pueden editar
          </span>
        )}
      </div>

      <div className="card">
        <form onSubmit={handleSubmit} className="grid">

          {/* Info del solicitante (solo lectura) */}
          <div style={{ background: '#0f1536', padding: '12px', borderRadius: '10px', border: '1px solid #4f6cf7' }}>
            <p className="kicker" style={{ margin: '0 0 4px 0' }}>Técnico solicitante</p>
            <p style={{ margin: 0, fontWeight: 'bold', color: '#e9eefc' }}>{solicitud.requesterName}</p>
          </div>

          {/* Rango de fechas */}
          <div>
            <p className="kicker" style={{ marginBottom: 8 }}>📅 Rango de fechas del trabajo</p>
            <div className="form-grid-2">
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Fecha de inicio *</label>
                <DatePicker
                  value={formData.fechaInicio}
                  onChange={(v) => handleChange('fechaInicio', v)}
                  placeholder="Fecha de inicio"
                  max={formData.fechaFin || undefined}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Fecha de fin *</label>
                <DatePicker
                  value={formData.fechaFin}
                  onChange={(v) => handleChange('fechaFin', v)}
                  placeholder="Fecha de fin"
                  min={formData.fechaInicio || undefined}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
              <button type="button" className="copy" onClick={setRangoHoy} style={{ padding: '4px 8px', fontSize: '11px' }}>Hoy</button>
              <button type="button" className="copy" onClick={setRangoEstaSemana} style={{ padding: '4px 8px', fontSize: '11px' }}>Esta semana</button>
              <button type="button" className="copy" onClick={setRangoProximaSemana} style={{ padding: '4px 8px', fontSize: '11px' }}>Próxima semana</button>
            </div>
            {formData.fechaInicio && formData.fechaFin && (
              <p className="meta" style={{ marginTop: 8, fontSize: 12, color: '#10b981' }}>✅ Rango: {formatRangoFechas()}</p>
            )}
          </div>

          {/* Números de referencia */}
          <div className="form-grid-2">
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Nº Oferta/Pedido</label>
              <input placeholder="Nº Oferta/Pedido" value={formData.numeroOferta} onChange={(e) => handleChange('numeroOferta', e.target.value)} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Nº Presupuesto</label>
              <input placeholder="Nº Presupuesto" value={formData.numeroPresupuesto} onChange={(e) => handleChange('numeroPresupuesto', e.target.value)} />
            </div>
          </div>

          {/* Código obra y partida */}
          <div className="form-grid-2">
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Código de Obra *</label>
              <input placeholder="Código de Obra *" value={formData.codigoObra} onChange={(e) => handleChange('codigoObra', e.target.value)} required />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Partida de Trabajo *</label>
              <input placeholder="Partida de Trabajo *" value={formData.partidaTrabajo} onChange={(e) => handleChange('partidaTrabajo', e.target.value)} required />
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Trabajos a Realizar *</label>
            <textarea placeholder="Trabajos a Realizar *" value={formData.trabajosRealizar} onChange={(e) => handleChange('trabajosRealizar', e.target.value)} rows={4} required />
          </div>

          {/* Operarios */}
          <div className="grid">
            <p className="kicker">👥 Operarios</p>
            <div style={{ marginBottom: 8 }}>
              <input
                placeholder="🔍 Buscar operario..."
                value={filtroOperarios}
                onChange={(e) => setFiltroOperarios(e.target.value)}
                style={inputStyle}
              />
            </div>
            <select
              onChange={(e) => {
                const op = operarios.find(o => o.id === parseInt(e.target.value));
                addOperario(op);
                e.target.value = "";
                setFiltroOperarios("");
              }}
              style={{ ...inputStyle, marginBottom: 8 }}
              defaultValue=""
            >
              <option value="" disabled>Seleccionar operario...</option>
              {operariosFiltrados.map(op => (
                <option key={op.id} value={op.id}>{op.nombre} — {op.categoria} ({op.especialidad})</option>
              ))}
            </select>

            {selectedOperarios.length > 0 && (
              <div style={{ background: '#0f1536', padding: '12px', borderRadius: '8px', border: '1px solid #4f6cf7', marginBottom: 8 }}>
                <p className="meta" style={{ margin: '0 0 8px 0' }}>Equipo ({selectedOperarios.length}):</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {selectedOperarios.map(op => (
                    <span key={op.id} className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: '#273067', padding: '6px 12px' }}>
                      <span>{op.nombre} <span className="meta">({op.categoria})</span></span>
                      <button type="button" className="copy" onClick={() => removeOperario(op.id)} style={{ padding: '2px 6px', fontSize: '11px' }}>✕</button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <input
              placeholder="Operarios Asignados *"
              value={formData.operariosAsignados}
              onChange={(e) => handleChange('operariosAsignados', e.target.value)}
              required
              style={{ borderColor: selectedOperarios.length > 0 ? '#10b981' : undefined }}
            />
          </div>

          {/* Dirección */}
          <div>
            <label style={{ display: 'block', fontSize: '12px', color: '#9ca3af', marginBottom: 4 }}>Dirección de la Obra *</label>
            <textarea placeholder="Dirección de la Obra *" value={formData.direccionObra} onChange={(e) => handleChange('direccionObra', e.target.value)} rows={2} required />
          </div>

          {/* Contacto */}
          <div className="form-grid-2">
            <input placeholder="Persona de Contacto *" value={formData.personaContacto} onChange={(e) => handleChange('personaContacto', e.target.value)} required />
            <input placeholder="Teléfono de Contacto" value={formData.telefonoContacto} onChange={(e) => handleChange('telefonoContacto', e.target.value)} type="tel" />
          </div>

          {/* Botones */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', paddingTop: 8, borderTop: '1px solid #273067' }}>
            <button type="submit" className="btn" disabled={loading}>
              {loading ? "Guardando..." : "💾 Guardar Cambios"}
            </button>
            <button type="button" className="copy" onClick={() => navigate(`/solicitud/${id}`)}>
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
